import { useLocale } from '@solinkcorp/locale';
import { cn } from '@solinkcorp/solink-ui/lib/utils';
import { useState } from 'react';
import {
  PREDEFINED_LAYOUTS,
  TEMPLATE_CATEGORY_ORDER,
  type PredefinedLayout,
  type PredefinedLayoutWidget,
  type TemplateCategory,
} from '../../../layout/predefinedLayouts';

interface PredefinedLayoutGalleryProps {
  selectedId: string | null;
  onSelect: (template: PredefinedLayout) => void;
}

export function PredefinedLayoutGallery({ selectedId, onSelect }: PredefinedLayoutGalleryProps) {
  const locale = useLocale();
  const [activeCategory, setActiveCategory] = useState<TemplateCategory | 'all'>('all');

  const filtered =
    activeCategory === 'all'
      ? PREDEFINED_LAYOUTS
      : PREDEFINED_LAYOUTS.filter((t) => t.category === activeCategory);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2">
        <FilterTab
          label={locale.serviceDisplayMonitor.allTemplates}
          active={activeCategory === 'all'}
          onClick={() => setActiveCategory('all')}
          data-testid="template-category-tab-all"
        />
        {TEMPLATE_CATEGORY_ORDER.map((cat) => (
          <FilterTab
            key={cat}
            label={cat}
            active={activeCategory === cat}
            onClick={() => setActiveCategory(cat)}
            data-testid={`template-category-tab-${cat}`}
          />
        ))}
      </div>

      {/* Disclaimer */}
      <p className="text-text-secondary caption">
        {locale.serviceDisplayMonitor.templateDisclaimer}
      </p>

      <div className="min-h-0 flex-1 overflow-y-auto pr-2 [scrollbar-gutter:stable]">
        <div className="grid grid-cols-2 gap-4 pb-1">
          {filtered.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              selected={selectedId === template.id}
              onClick={() => onSelect(template)}
              data-testid={`template-card-${template.id}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function FilterTab({
  label,
  active,
  onClick,
  'data-testid': testId,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  'data-testid'?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={cn(
        'rounded-sm px-2 py-1 transition-colors label-sm',
        active
          ? 'bg-interaction-primary-background-default text-interaction-primary-content-default'
          : 'border border-border-muted text-interaction-neutral-content-default hover:bg-background-elevated',
      )}
    >
      {label}
    </button>
  );
}

interface TemplateCardProps {
  template: PredefinedLayout;
  selected: boolean;
  onClick: () => void;
  'data-testid'?: string;
}

function TemplateCard({ template, selected, onClick, 'data-testid': testId }: TemplateCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={cn(
        'flex flex-col gap-2.5 rounded-lg text-left transition-colors',
        'border bg-background-surface',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        selected ? 'border-2 border-border-accent p-[15px]' : 'border border-muted p-4',
      )}
    >
      <div className="flex flex-col">
        <span className="truncate text-text-primary heading-sm" title={template.name}>
          {template.name}
        </span>
        <span className="truncate text-foreground-secondary body-md" title={template.description}>
          {template.description}
        </span>
      </div>
      <WidgetPreviewLayout widgets={template.widgets} />
    </button>
  );
}

const PAD = 10;
const GAP = 2;

function WidgetPreviewLayout({ widgets }: { widgets: ReadonlyArray<PredefinedLayoutWidget> }) {
  return (
    <div className="relative h-[144px] w-full overflow-hidden rounded border border-border-muted bg-background">
      {widgets.map(({ position: pos }, i) => {
        const hasLeft = pos.x > 0;
        const hasRight = pos.x + pos.w < 100;
        const hasTop = pos.y > 0;
        const hasBottom = pos.y + pos.h < 100;

        return (
          <div
            key={i}
            className="absolute rounded-sm bg-background-elevated"
            style={{
              left: `calc((100% - ${PAD * 2}px) * ${pos.x / 100} + ${PAD + (hasLeft ? GAP : 0)}px)`,
              top: `calc((100% - ${PAD * 2}px) * ${pos.y / 100} + ${PAD + (hasTop ? GAP : 0)}px)`,
              right: `calc((100% - ${PAD * 2}px) * ${(100 - pos.x - pos.w) / 100} + ${PAD + (hasRight ? GAP : 0)}px)`,
              bottom: `calc((100% - ${PAD * 2}px) * ${(100 - pos.y - pos.h) / 100} + ${PAD + (hasBottom ? GAP : 0)}px)`,
            }}
          />
        );
      })}
    </div>
  );
}
